import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  STORAGE_KEY,
  applyScheme,
  readStoredMode,
  resolveScheme,
} from '../config/colors';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readStoredMode);
  const [scheme, setScheme] = useState(() => resolveScheme(readStoredMode()));

  useEffect(() => {
    const next = resolveScheme(mode);
    applyScheme(next);
    setScheme(next);
  }, [mode]);

  useEffect(() => {
    if (mode !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const next = resolveScheme('system');
      applyScheme(next);
      setScheme(next);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  const setMode = useCallback((next) => {
    const resolved = resolveScheme(next);
    applyScheme(resolved);
    setModeState(next);
    setScheme(resolved);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persistence failure is non-fatal.
    }
  }, []);

  const toggle = useCallback(() => {
    setMode(scheme === 'dark' ? 'light' : 'dark');
  }, [scheme, setMode]);

  const value = useMemo(
    () => ({
      scheme,
      mode,
      isSystem: mode === 'system',
      setMode,
      toggle,
    }),
    [scheme, mode, setMode, toggle],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}

export default ThemeProvider;
